import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { User } from '@client/components/_ui/display/User';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs } from '@client/trpc/client';
import {
    SERVICE_API_TOKEN_EXPIRY_WARNING_DAYS,
    SERVICE_API_TOKEN_SCOPE_LABELS,
    type ServiceApiTokenScope,
} from '@shared/constants/system/service-api-token';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Ban, CalendarClock, Key, Plus, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { RenewTokenModal } from './components/RenewTokenModal';
import { RevokeTokenModal } from './components/RevokeTokenModal';
import { RotateTokenModal } from './components/RotateTokenModal';
import { ServiceApiDisabledCallout } from './components/ServiceApiDisabledCallout';
import { ServiceApiTokenFilters } from './components/ServiceApiTokenFilters';
import {
    serviceApiTokenDaysUntilExpiry,
    serviceApiTokenRowActions,
    serviceApiTokenStatus,
} from './components/service-api-token-expiry';
import { TokenSecretModal } from './components/TokenSecretModal';
import { type ServiceApiTokenSecret, TokenSecretContext } from './components/token-secret-context';

export const Route = createFileRoute('/console/$orgId/configuration/api-integrations/api-tokens')({
    staticData: {
        breadcrumb: { title: 'API tokens' },
        ui: {
            icon: Key,
            title: 'API tokens',
            subtitle: 'Manage service API tokens for external integrations',
            menuDescription: 'Manage API tokens for external system integrations',
        },
        permissions: ['service_api_token:read'],
    },
    component: ApiTokensPage,
});

function ApiTokensPage() {
    const { orgId } = Route.useParams();
    const navigate = useNavigate();
    const user = useUser();
    const canCreate = hasPermission(user.roles, 'service_api_token:create') && !user.isSupportAccount;
    const canUpdate = hasPermission(user.roles, 'service_api_token:update') && !user.isSupportAccount;
    const canDelete = hasPermission(user.roles, 'service_api_token:delete');

    const [revokeToken, setRevokeToken] = useState<{ id: bigint; name: string } | null>(null);
    const [renewToken, setRenewToken] = useState<{ id: bigint; name: string } | null>(null);
    const [rotateToken, setRotateToken] = useState<{ id: bigint; name: string } | null>(null);
    // The create drawer is the `create` child route; it hands the one-time secret back through
    // this context, and the modal shows it once the drawer has closed.
    const [newTokenSecret, setNewTokenSecret] = useState<ServiceApiTokenSecret | null>(null);

    return (
        <PageController route={Route}>
            <ApiTokensContent
                canCreate={canCreate}
                canUpdate={canUpdate}
                canDelete={canDelete}
                onCreateClick={() =>
                    navigate({
                        to: '/console/$orgId/configuration/api-integrations/api-tokens/create',
                        params: { orgId },
                    })
                }
                onRevokeClick={(id, name) => setRevokeToken({ id, name })}
                onRenewClick={(id, name) => setRenewToken({ id, name })}
                onRotateClick={(id, name) => setRotateToken({ id, name })}
            />

            <TokenSecretContext.Provider value={setNewTokenSecret}>
                <Outlet />
            </TokenSecretContext.Provider>

            <RevokeTokenModal token={revokeToken} onClose={() => setRevokeToken(null)} />

            <RenewTokenModal token={renewToken} onClose={() => setRenewToken(null)} />

            <RotateTokenModal
                token={rotateToken}
                onClose={() => setRotateToken(null)}
                onSuccess={(token, name, expiresAt) => {
                    setRotateToken(null);
                    setNewTokenSecret({ token, name, expiresAt });
                }}
            />

            <TokenSecretModal tokenInfo={newTokenSecret} onClose={() => setNewTokenSecret(null)} />
        </PageController>
    );
}

interface ApiTokensContentProps {
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    onCreateClick: () => void;
    onRevokeClick: (id: bigint, name: string) => void;
    onRenewClick: (id: bigint, name: string) => void;
    onRotateClick: (id: bigint, name: string) => void;
}

function ApiTokensContent({
    canCreate,
    canUpdate,
    canDelete,
    onCreateClick,
    onRevokeClick,
    onRenewClick,
    onRotateClick,
}: ApiTokensContentProps) {
    const { trpc } = useTrpc();
    const { formatDate, formatDateTime } = useFormatter();

    const [queryParams, setQueryParams] = useState<RouterInputs['serviceApiToken']['list']>({
        orderBy: { createdAt: 'desc' },
    });

    // Handle filter changes
    const handleFilterChange = useCallback(
        (
            where: NonNullable<RouterInputs['serviceApiToken']['list']['where']>,
            status: RouterInputs['serviceApiToken']['list']['status'],
        ) => {
            setQueryParams((prev) => ({
                ...prev,
                where,
                status,
                offset: 0, // Reset pagination when filters change
            }));
        },
        [],
    );

    const { data, isLoading } = trpc.serviceApiToken.list.useQuery(queryParams);

    // Token management (create / rotate / renew) is refused server-side for orgs whose Service API is
    // switched off in the system console. Read the flag up front so the page explains the state
    // instead of surfacing the refusal only after the user submits. Revoke stays available.
    // Fails open: the server guard is authoritative, so while the flag is loading or unavailable the
    // actions stay usable rather than silently locking the page.
    const { data: enablement } = trpc.serviceApiToken.isServiceApiEnabled.useQuery();
    const serviceApiDisabled = enablement?.enabled === false;
    const canManage = canUpdate && !serviceApiDisabled;

    const columns = [
        {
            key: 'name',
            title: 'Name',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-subtle-foreground" />
                    <span className="font-medium">{record.name}</span>
                </div>
            ),
        },
        {
            key: 'tokenPrefix',
            title: 'Token',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <code className="rounded bg-muted px-2 py-1 text-xs">{record.tokenPrefix}...</code>
            ),
        },
        {
            key: 'scopes',
            title: 'Scopes',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <div className="flex flex-wrap gap-1">
                    {(record.scopes as ServiceApiTokenScope[]).map((scope) => (
                        <Tag key={scope} className="text-xs" variant="outlined">
                            {SERVICE_API_TOKEN_SCOPE_LABELS[scope] ?? scope}
                        </Tag>
                    ))}
                </div>
            ),
        },
        {
            key: 'isActive',
            title: 'Status',
            // Render time, so between refetches a row can read Expired under the Active filter; the next
            // refetch applies the Status filter at the server's request time and moves it.
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => (
                <StatusBadge status={serviceApiTokenStatus(record)} module="serviceApiToken" variant="outlined" />
            ),
        },
        {
            key: 'expiresAt',
            title: 'Expires',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => {
                const daysUntilExpiry = serviceApiTokenDaysUntilExpiry(record.expiresAt);
                const isExpired = daysUntilExpiry <= 0;
                const isExpiringSoon = !isExpired && daysUntilExpiry <= SERVICE_API_TOKEN_EXPIRY_WARNING_DAYS;
                return (
                    <span
                        className={
                            isExpired ? 'text-status-danger' : isExpiringSoon ? 'text-status-warning' : undefined
                        }
                    >
                        {formatDate(record.expiresAt)}
                        {isExpiringSoon && ' (expires soon)'}
                    </span>
                );
            },
        },
        {
            key: 'lastUsedAt',
            title: 'Last used',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => {
                if (!record.lastUsedAt) return <span className="text-subtle-foreground">Never</span>;
                return (
                    <Tooltip title={record.lastUsedIp ? `IP: ${record.lastUsedIp}` : undefined}>
                        <span>{formatDateTime(record.lastUsedAt)}</span>
                    </Tooltip>
                );
            },
        },
        {
            key: 'createdAt',
            title: 'Created at',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) =>
                formatDateTime(record.createdAt),
        },
        {
            key: 'createdBy',
            title: 'Created by',
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) =>
                record.createdBy ? (
                    <User
                        userId={BigInt(record.createdBy.id)}
                        name={[record.createdBy.firstName, record.createdBy.lastName].filter(Boolean).join(' ')}
                    />
                ) : null,
        },
        {
            key: 'actions',
            title: '',
            width: 160,
            render: (_: unknown, record: NonNullable<typeof data>['tokens'][number]) => {
                const actions = serviceApiTokenRowActions(record, { canManage, canDelete });
                return (
                    <Space size="small">
                        {actions.renew && (
                            <Button.Icon
                                icon={<CalendarClock />}
                                color="orange"
                                size="small"
                                tooltip="Renew token"
                                aria-label="Renew token"
                                onClick={() => onRenewClick(record.id, record.name)}
                            />
                        )}
                        {actions.rotate && (
                            <Button.Icon
                                icon={<RotateCcw />}
                                color="gray"
                                size="small"
                                tooltip="Rotate token"
                                aria-label="Rotate token"
                                onClick={() => onRotateClick(record.id, record.name)}
                            />
                        )}
                        {actions.revoke && (
                            <Button.Icon
                                icon={<Ban />}
                                color="red"
                                size="small"
                                tooltip="Revoke token"
                                aria-label="Revoke token"
                                onClick={() => onRevokeClick(record.id, record.name)}
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    const tableProps = usePrismaTable({
        data: data?.tokens ?? [],
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No API tokens yet' },
    });

    return (
        <div className="space-y-4">
            {serviceApiDisabled && <ServiceApiDisabledCallout />}
            {canCreate && (
                <div className="flex items-center justify-end">
                    <Button
                        type="primary"
                        icon={<Plus />}
                        onClick={onCreateClick}
                        disabled={serviceApiDisabled}
                        disabledReason="Contact Curium to enable the Service API for this organisation"
                    >
                        Create token
                    </Button>
                </div>
            )}

            <ServiceApiTokenFilters onChange={handleFilterChange} />

            <PrismaTable {...tableProps} size="small" />
        </div>
    );
}
